import React from 'react';
import styles from './UserProfile.module.css';
import { personIcon } from '../../assets/icons';
import { FaEllipsisH } from 'react-icons/fa';
import EditableField from '../utils/EditableField';
import SharableUsername from '../utils/SharableUsername';
import EditProfileModal from '../utils/EditProfileModal';
import ProfileLoading from '../utils/ProfileLoading';
import OwnedTabCharacters from './OwnedTabCharacters';
import OwnedTabPersonas from './OwnedTabPersonas';
import OwnedTabScenarios from './OwnedTabScenarios';
import modalStyles from '../utils/EditProfileModal.module.css';
import { ProfileComponentProps } from './UserProfile';

const DesktopUserProfile: React.FC<ProfileComponentProps> = ({
  profileData,
  isCurrentUser,
  isUpdating,
  displayName,
  usernameField,
  activeTab,
  characters,
  personas,
  scenarios,
  tabsLoading,
  menuOpen,
  handleIconChange,
  handleSignOut,
  toggleMenu,
  setActiveTab,
  refreshCharacters,
  refreshPersonas,
  refreshScenarios,
  handleBack,
  loading
}) => {
  if (loading) {
    return <ProfileLoading onBack={handleBack} />;
  }

  if (!profileData) {
    return <div>User not found</div>;
  }

  const renderDisplayName = (value: string): JSX.Element => (
    <h1 className={styles.displayName}>{value}</h1>
  );

  const renderUsername = (value: string): JSX.Element => (
    <SharableUsername username={value} type="user" />
  );

  const renderTabContent = (): JSX.Element | null => {
    if (activeTab === 'characters') {
      return <OwnedTabCharacters characters={characters} isLoading={tabsLoading} onRefresh={refreshCharacters} />;
    } else if (activeTab === 'personas') {
      return <OwnedTabPersonas personas={personas as any} isLoading={tabsLoading} onRefresh={refreshPersonas} />;
    } else if (activeTab === 'scenarios') {
      return <OwnedTabScenarios scenarios={scenarios} isLoading={tabsLoading} onRefresh={refreshScenarios} />;
    }
    return null;
  };

  return (
    <div className={styles.wrapper}>
      <div 
        className={styles.backgroundImage} 
        style={{ 
          backgroundImage: `url(${profileData.icon || '/hangjam.png'})`,
          transition: 'filter 0.3s ease'
        }}
      />
      <div className={styles.container}>
        {isCurrentUser && (
          <div className={styles.menuContainer}>
            <button 
              className={styles.menuButton}
              onClick={toggleMenu}
            >
              <FaEllipsisH />
            </button>
            {menuOpen && (
              <>
                <div className={styles.menuOverlay} onClick={toggleMenu} />
                <div className={styles.menuDropdown}>
                  <button 
                    className={styles.menuItem}
                    onClick={() => {
                      handleSignOut();
                      toggleMenu();
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <div className={styles.profileSection}>
          <div className={styles.iconWrapper}>
            <img 
              src={profileData.icon || personIcon} 
              alt={profileData.display_name} 
              className={styles.userIcon}
            />
            {isCurrentUser && (
              <label className={`${styles.editIconButton} ${isUpdating ? styles.updating : ''}`} htmlFor="desktop-icon-upload">
                {isUpdating ? (
                  <div className={styles.spinner} />
                ) : (
                  <span className={styles.editIconSymbol}>✎</span>
                )}
                <input
                  type="file"
                  id="desktop-icon-upload"
                  accept="image/*"
                  onChange={handleIconChange}
                  style={{ display: 'none' }}
                  disabled={isUpdating}
                />
              </label>
            )}
          </div>
          <EditableField
            value={profileData.display_name}
            onEdit={displayName.startEdit}
            isEditing={displayName.isEditing}
            isLoading={displayName.isLoading}
            error={displayName.error}
            renderDisplay={renderDisplayName}
            containerClassName={styles.displayNameContainer}
            isVisible={isCurrentUser}
          />
          <EditableField
            value={profileData.username}
            onEdit={usernameField.startEdit}
            isEditing={usernameField.isEditing}
            isLoading={usernameField.isLoading}
            error={usernameField.error}
            renderDisplay={renderUsername}
            containerClassName={styles.usernameContainer}
            isVisible={isCurrentUser}
          />
        </div>

        {isCurrentUser && (
          <>
            <div className={styles.tabs}>
              <button
                className={`${styles.tabButton} ${activeTab === 'characters' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('characters')}
              >
                Characters
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'personas' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('personas')}
              >
                Personas
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'scenarios' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('scenarios')}
              >
                Scenarios
              </button>
            </div>
            <div className={styles.tabContentWrapper}>
              {renderTabContent()}
            </div>
          </>
        )}
      </div>

      <EditProfileModal
        title="Edit Display Name"
        isOpen={displayName.isEditing}
        onClose={() => displayName.setIsEditing(false)}
        onSubmit={displayName.handleSubmit}
        isLoading={displayName.isLoading}
        error={displayName.error}
      >
        <input
          type="text"
          value={displayName.value}
          onChange={(e) => displayName.setValue(e.target.value)}
          placeholder="Enter new display name"
          className={modalStyles.nameInput}
          disabled={displayName.isLoading}
        />
      </EditProfileModal>

      <EditProfileModal
        title="Edit Username"
        isOpen={usernameField.isEditing}
        onClose={() => usernameField.setIsEditing(false)}
        onSubmit={usernameField.handleSubmit}
        isLoading={usernameField.isLoading}
        error={usernameField.error}
      >
        <div className={modalStyles.inputGroup}>
          <span className={modalStyles.inputPrefix}>@</span>
          <input
            type="text"
            value={usernameField.value}
            onChange={(e) => usernameField.setValue(e.target.value)}
            placeholder="Enter new username"
            className={modalStyles.usernameInput}
            disabled={usernameField.isLoading}
          />
        </div>
      </EditProfileModal>
    </div>
  );
};

export default DesktopUserProfile; 